/**
 * RecurringSubscriptionDialog - Dialog to manage recurring subscriptions
 * Extracted from CustomersPage.js
 */
import React from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const RecurringSubscriptionDialog = ({
  open,
  onOpenChange,
  recurringSubscriptions,
  activeSubscription,
  setActiveSubscription,
  subscriptionFormData,
  setSubscriptionFormData,
  services,
  customerDetails,
  onCreateSubscription,
  onUpdateSubscription,
  onCancelSubscription,
  onRefreshSubscriptions,
  onRefreshCustomerDetails,
  selectedCustomer
}) => {
  
  const handlePause = async () => {
    const token = localStorage.getItem('token');
    try {
      await axios.post(
        `${API}/recurring-subscriptions/${activeSubscription.id}/pause`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Abo pausiert');
      if (onRefreshSubscriptions) await onRefreshSubscriptions(customerDetails.id);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Fehler beim Pausieren');
    }
  };

  const handleSkipMonth = async () => {
    const token = localStorage.getItem('token');
    try {
      await axios.post(
        `${API}/recurring-subscriptions/${activeSubscription.id}/skip-next`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Nächster Monat wird übersprungen');
      if (onRefreshSubscriptions) await onRefreshSubscriptions(customerDetails.id);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Fehler beim Überspringen');
    }
  };

  const handleResume = async () => {
    const token = localStorage.getItem('token');
    try {
      await axios.post(
        `${API}/recurring-subscriptions/${activeSubscription.id}/resume`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Abo fortgesetzt');
      if (onRefreshSubscriptions) await onRefreshSubscriptions(customerDetails.id);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Fehler beim Fortsetzen');
    }
  };

  const handleDeletePending = async (sub) => {
    if (!window.confirm('Dieses ausstehende Abo wirklich löschen?')) return;
    try {
      const token = localStorage.getItem('token');
      await axios.delete(
        `${API}/recurring-subscriptions/${sub.id}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Abo gelöscht');
      if (onRefreshSubscriptions) await onRefreshSubscriptions(customerDetails.id);
      if (onRefreshCustomerDetails && selectedCustomer) await onRefreshCustomerDetails(selectedCustomer);
    } catch (error) {
      console.error('Error deleting subscription', error);
      const msg = error.response?.data?.detail || error.message || 'Fehler beim Löschen';
      if (error.response?.status !== 200) {
        toast.error(msg);
      } else {
        toast.success('Abo gelöscht');
        if (onRefreshSubscriptions) onRefreshSubscriptions(customerDetails.id);
      }
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Wiederkehrende Abos verwalten</DialogTitle>
        </DialogHeader>
        
        <div className="space-y-4">
          {/* Show list of current subscriptions incl. Status-Hinweis */}
          {recurringSubscriptions && recurringSubscriptions.length > 0 && (
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-4 space-y-2">
              {/* Liste aller Abos */}
              <div className="space-y-2">
                {recurringSubscriptions.map((sub) => (
                  <div
                    key={sub.id}
                    className={`flex justify-between items-center px-3 py-2 rounded border cursor-pointer ${
                      activeSubscription && activeSubscription.id === sub.id
                        ? 'bg-blue-100 border-blue-400'
                        : 'bg-white border-blue-200 hover:bg-blue-50'
                    }`}
                    onClick={() => setActiveSubscription(sub)}
                  >
                    <div className="text-sm">
                      <p className="font-semibold text-blue-900">{sub.name}</p>
                      <p className="text-xs text-gray-600">
                        Start: {new Date(sub.start_date).toLocaleDateString('de-CH')}
                        {sub.renewal_mode === 'monthly_25' && sub.next_billing_date && (
                          <> · Nächste Abbuchung: {new Date(sub.next_billing_date).toLocaleDateString('de-CH')}</>
                        )}
                        {sub.renewal_mode === 'on_depletion' && (
                          <> · Auto-Reload bei Aufbrauch</>
                        )}
                      </p>
                    </div>
                    <div className="flex items-center gap-2">
                      <Badge variant={sub.status === 'active' ? 'success' : 'outline'}>
                        {sub.status === 'pending'
                          ? 'Initalzahlung ausstehend'
                          : sub.status === 'cancelled'
                          ? 'Gekündigt'
                          : sub.status === 'paused'
                          ? 'Pausiert'
                          : 'Aktiv'}
                      </Badge>
                      {sub.status === 'pending' && (
                        <Button
                          size="sm"
                          variant="ghost"
                          className="text-red-600 hover:text-red-700 hover:bg-red-50"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleDeletePending(sub);
                          }}
                        >
                          🗑️
                        </Button>
                      )}
                    </div>
                  </div>
                ))}
              </div>

              {/* Status-Hinweise für das aktuell ausgewählte Abo */}
              {activeSubscription && activeSubscription.status === 'pending' && (
                <p className="text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded p-2">
                  Die erste Zahlung wurde gestartet, ist aber noch nicht bestätigt. Nach erfolgreicher Bestätigung wird das Abo automatisch aktiviert und die initialen Punkte werden gutgeschrieben.
                </p>
              )}
              {activeSubscription && activeSubscription.status === 'cancelled' && (
                <p className="text-xs text-red-800 bg-red-50 border border-red-200 rounded p-2">
                  Dieses Abo wurde gekündigt. Es finden keine weiteren Abbuchungen oder Punktegutschriften mehr statt.
                </p>
              )}
              {activeSubscription && activeSubscription.status === 'paused' && (
                <p className="text-xs text-orange-800 bg-orange-50 border border-orange-200 rounded p-2">
                  Dieses Abo ist pausiert. Es werden aktuell keine Abbuchungen durchgeführt, bis es wieder fortgesetzt wird.
                </p>
              )}
            </div>
          )}

          {/* Subscription Form */}
          <div className="space-y-4">
            <div>
              <Label htmlFor="sub-name">Abo-Name *</Label>
              <Input
                id="sub-name"
                value={subscriptionFormData.name}
                onChange={(e) => setSubscriptionFormData({...subscriptionFormData, name: e.target.value})}
                placeholder="z.B. Monatsabo 100 Punkte"
              />
            </div>

            <div>
              <Label htmlFor="sub-description">Beschreibung</Label>
              <Input
                id="sub-description"
                value={subscriptionFormData.description}
                onChange={(e) => setSubscriptionFormData({...subscriptionFormData, description: e.target.value})}
                placeholder="Optional: Zusätzliche Informationen"
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="sub-points">Punkte pro Monat *</Label>
                <Input
                  id="sub-points"
                  type="number"
                  min="1"
                  value={subscriptionFormData.points_per_month}
                  onChange={(e) => setSubscriptionFormData({...subscriptionFormData, points_per_month: parseInt(e.target.value) || 0})}
                />
              </div>

              <div>
                <Label htmlFor="sub-price">Preis pro Monat (CHF) *</Label>
                <Input
                  id="sub-price"
                  type="number"
                  min="0"
                  step="0.01"
                  value={subscriptionFormData.price_per_month}
                  onChange={(e) => setSubscriptionFormData({...subscriptionFormData, price_per_month: parseFloat(e.target.value) || 0})}
                />
              </div>
            </div>

            {/* Flexible Initialzahlung */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="sub-initial-price" className="flex items-center gap-1">
                  Initialbetrag (CHF, einmalig)
                  <span
                    className="text-xs text-gray-500 cursor-default"
                    title="Dieser Betrag wird sofort abgebucht (Startzahlung). Leer lassen = gleicher Betrag wie Monatsbetrag."
                  >
                    ⓘ
                  </span>
                </Label>
                <Input
                  id="sub-initial-price"
                  type="number"
                  min="0"
                  step="0.01"
                  value={subscriptionFormData.initial_price ?? ''}
                  onChange={(e) => setSubscriptionFormData({
                    ...subscriptionFormData,
                    initial_price: e.target.value === '' ? null : parseFloat(e.target.value) || 0
                  })}
                  placeholder="Leer lassen = wie Monatsbetrag"
                />
              </div>
              <div>
                <Label htmlFor="sub-initial-points" className="flex items-center gap-1">
                  Initiale Punkte (einmalig)
                  <span
                    className="text-xs text-gray-500 cursor-default"
                    title="Einmalige Punktegutschrift nach erfolgreicher Initialzahlung (zusätzlich zu den monatlichen Punkten)."
                  >
                    ⓘ
                  </span>
                </Label>
                <Input
                  id="sub-initial-points"
                  type="number"
                  min="0"
                  value={subscriptionFormData.initial_points ?? ''}
                  onChange={(e) => setSubscriptionFormData({
                    ...subscriptionFormData,
                    initial_points: e.target.value === '' ? null : parseInt(e.target.value) || 0
                  })}
                  placeholder="z.B. 150 (optional)"
                />
              </div>
            </div>

            <div>
              <Label htmlFor="sub-payment-method">Zahlungsmethode</Label>
              <div className="p-3 bg-gray-50 border rounded-lg">
                <div className="flex items-center gap-2 text-sm">
                  <span className="text-2xl">💳</span>
                  <div>
                    <div className="font-semibold">Kreditkarte (via Payrexx)</div>
                    <div className="text-xs text-gray-600">Wiederkehrende Zahlungen werden automatisch abgebucht</div>
                  </div>
                </div>
              </div>
            </div>

            <div>
              <Label htmlFor="sub-billing-cycle">Abrechnungszyklus</Label>
              <Select
                value={subscriptionFormData.billing_cycle}
                onValueChange={(value) => setSubscriptionFormData({...subscriptionFormData, billing_cycle: value})}
              >
                <SelectTrigger id="sub-billing-cycle">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="monthly">Monatlich</SelectItem>
                  <SelectItem value="quarterly">Vierteljährlich</SelectItem>
                  <SelectItem value="yearly">Jährlich</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Renewal Mode (Feature B) */}
            <div className="border-t pt-4">
              <Label htmlFor="sub-renewal-mode">Verlängerungsmodus</Label>
              <Select
                value={subscriptionFormData.renewal_mode}
                onValueChange={(value) => setSubscriptionFormData({...subscriptionFormData, renewal_mode: value})}
              >
                <SelectTrigger id="sub-renewal-mode">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="monthly_25">Monatlich am 25. (Fixdatum)</SelectItem>
                  <SelectItem value="on_depletion">Bei Punkteverbrauch (Auto-Reload)</SelectItem>
                </SelectContent>
              </Select>
              <p className="text-xs text-gray-600 mt-1">
                {subscriptionFormData.renewal_mode === 'monthly_25' 
                  ? '📅 Automatische Abbuchung jeden Monat am 25.' 
                  : '🔄 Automatische Aufladung wenn Punkte aufgebraucht sind'}
              </p>
            </div>

            {/* Service Restrictions (Optional) */}
            <div>
              <Label>Service-Einschränkungen (Optional)</Label>
              <p className="text-sm text-gray-600 mb-2">Wählen Sie Services aus, für die diese Punkte verwendet werden dürfen. Leer = Alle Services erlaubt.</p>
              <div className="max-h-48 overflow-y-auto border rounded p-3 space-y-2">
                {services.map((service) => {
                  const isChecked = subscriptionFormData.allowed_service_ids.includes(service.id);
                  return (
                    <label key={service.id} className="flex items-center space-x-2 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={isChecked}
                        onChange={(e) => {
                          if (e.target.checked) {
                            setSubscriptionFormData({
                              ...subscriptionFormData,
                              allowed_service_ids: [...subscriptionFormData.allowed_service_ids, service.id]
                            });
                          } else {
                            setSubscriptionFormData({
                              ...subscriptionFormData,
                              allowed_service_ids: subscriptionFormData.allowed_service_ids.filter(id => id !== service.id)
                            });
                          }
                        }}
                        className="rounded"
                      />
                      <span className="text-sm">{service.name} (CHF {service.price?.toFixed(2)})</span>
                    </label>
                  );
                })}
              </div>
              <p className="text-xs text-gray-500 mt-2">
                {subscriptionFormData.allowed_service_ids.length === 0 
                  ? 'Alle Services erlaubt (keine Einschränkung)' 
                  : `${subscriptionFormData.allowed_service_ids.length} Service(s) ausgewählt`}
              </p>
            </div>
          </div>

          {/* Action Buttons */}
          <div className="flex justify-between gap-2 pt-4 border-t">
            <div className="flex gap-2">
              {activeSubscription && activeSubscription.status === 'active' && (
                <>
                  <Button variant="outline" onClick={handlePause}>
                    Pausieren
                  </Button>
                  <Button variant="outline" onClick={handleSkipMonth}>
                    Monat überspringen
                  </Button>
                  <Button variant="destructive" onClick={onCancelSubscription}>
                    Abo kündigen
                  </Button>
                </>
              )}
              {activeSubscription && activeSubscription.status === 'paused' && (
                <div className="flex gap-2">
                  <Button variant="outline" onClick={handleResume}>
                    Abo fortsetzen
                  </Button>
                  <Button variant="destructive" onClick={onCancelSubscription}>
                    Abo kündigen
                  </Button>
                </div>
              )}
            </div>
            <div className="flex gap-2">
              <Button variant="outline" onClick={() => onOpenChange(false)}>
                Abbrechen
              </Button>
              <Button 
                onClick={activeSubscription && activeSubscription.status === 'active' 
                  ? onUpdateSubscription 
                  : onCreateSubscription}
                disabled={!subscriptionFormData.name || subscriptionFormData.points_per_month <= 0 || subscriptionFormData.price_per_month <= 0}
              >
                {activeSubscription && activeSubscription.status === 'active' ? 'Abo aktualisieren' : 'Abo erstellen'}
              </Button>
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default RecurringSubscriptionDialog;
